import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import '@fortawesome/fontawesome-free/css/all.min.css';
import { Container, Row, Col, Button } from "reactstrap";
import MetaTags from "react-meta-tags";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import "./BusinessProfile.css";
import PlanSelectionModal from "../Components/PlanSelectionModal";
import QRCode from "react-qr-code";
import {getSubscription} from "../BaseUrl/SubscriptionApi";


const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  }
});

const mapContainerStyle = {
  height: "400px",
  width: "100%",
};

const BusinessProfile = () => {
  const [businessData, setBusinessData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false); 
  const history = useHistory();

  const [subscription, setSubscription] = useState(null);
  const [bannerData, setBannerData] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState({
    type: '',
    method: '',
    hasPaymentCard: false
  });
  const [modalConfig, setModalConfig] = useState({ 
    skipPlanSelection: false, 
    selectedPlan: null, 
    action: 'upgrade' 
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);


  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  // Handle banner button click with smart modal logic
  const handleBannerButtonClick = () => {
    // Check subscription and payment card status
    const hasSubscription = subscriptionDetails.type !== '' && subscriptionDetails.type !== 'free';
    const hasPaymentCard = subscriptionDetails.hasPaymentCard;
    
    if (hasSubscription) {
      // User has subscription - handle Change Plan flow
      if (hasPaymentCard) {
        // Scenario: Card exists - just select package and skip card details
        setModalConfig({ 
          skipPlanSelection: false, 
          selectedPlan: null, 
          action: 'changePlan', 
          skipCardDetails: true 
        });
        setIsModalOpen(true);
      } else {
        // Scenario: No card - select package and go to card details
        setModalConfig({ 
          skipPlanSelection: false, 
          selectedPlan: null, 
          action: 'changePlan', 
          skipCardDetails: false 
        });
        setIsModalOpen(true);
      }
    } else {
      // No subscription - normal flow
      setModalConfig({ 
        skipPlanSelection: false, 
        selectedPlan: null, 
        action: 'upgrade' 
      });
      setIsModalOpen(true);
    }
  };

  const fetchSubscription = async (token) => {
    try {
      const response = await axiosInstance.get("/business/payment/subscription", {
        headers: { Authorization: `Bearer ${token}` }
      });

      const subscriptionData = response.data?.responseData?.subscription;
      
      // Check if subscription is null (free plan)
      if (!subscriptionData) {
        setBannerData({
          message: "You are currently on a Free plan. Upgrade your membership to publish more deals",
          link: "/add-card-details",
          buttonText: "Add Plan"
        });
        setSubscriptionDetails({
          type: 'free',
          method: 'Not available',
          hasPaymentCard: false
        });
        return;
      }

      // Get subscription plan from subscription type
      const subscriptionPlan = response.data?.responseData?.subscriptionType || 'standard';
      
      const subscriptionBanners = {
        standard: {
          message: "You are on the Standard plan. Upgrade your membership to unlock more features.",
          link: "/upgrade-subscription",
          buttonText: "Upgrade Plan"
        },
        sponsored: {
          message: "You are on the Sponsored plan. Upgrade your membership to unlock more features.",
          link: "/upgrade-subscription",
          buttonText: "Upgrade Plan"
        },
      };

      setBannerData(subscriptionBanners[subscriptionPlan] || subscriptionBanners.standard);
      
      // Check if payment card exists (same logic as Subscription Details page)
      const hasPaymentCard = subscriptionData?.paymentCard !== null && subscriptionData?.paymentCard !== undefined;
      const paymentMethod = hasPaymentCard ? subscriptionData.subscriptionMethod : 'Not available';
      
      setSubscriptionDetails({
        type: subscriptionPlan,
        method: paymentMethod,
        hasPaymentCard: hasPaymentCard // Add this flag for easier checking
      });

    } catch (err) {
      // If API returns 404 or subscription is null, treat as free plan
      if (err.response?.status === 404 || err.response?.data?.responseCode === 404) {
        setBannerData({
          message: "You are currently on a Free plan. Upgrade your membership to publish more deals",
          link: "/add-card-details",
          buttonText: "Add Plan"
        });
        setSubscriptionDetails({
          type: 'free',
          method: 'Not available',
          hasPaymentCard: false
        });
      } else {
        console.error("Error fetching subscription:", err);
        // Fallback to free plan on other errors
        setBannerData({
          message: "You are currently on a Free plan. Upgrade your membership to publish more deals",
          link: "/add-card-details",
          buttonText: "Add Plan"
        });
        setSubscriptionDetails({
          type: 'free',
          method: 'Not available',
          hasPaymentCard: false
        });
      }
    } finally {
      // Reset refreshing flag when subscription fetch completes
      setIsRefreshing(false);
    }
  };

  // Function to refresh subscription data
  const refreshSubscriptionData = () => {
    setIsRefreshing(true);
    setRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
  const token = localStorage.getItem("accessToken");
  if (!token) {
    setError("No authentication token found. Please log in.");
    toast.error("No authentication token found. Please log in.");
    history.push("/login");
    return;
  }

  // Fetch Business Profile
  fetchBusinessProfile(token);

  // Fetch Subscription with new logic
  fetchSubscription(token);

  // Keep the old subscription fetch for compatibility
  const fetchSub = async () => {
    try {
      const res = await getSubscription();
      setSubscription(res?.responseData || null);
    } catch (err) {
      console.error("Failed to fetch subscription:", err);
    }
  };

  fetchSub();
}, [history]);


  const fetchBusinessProfile = async (token) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/business/profile", {
        headers: { Authorization: `Bearer ${token}` }
      });

      setBusinessData(response.data?.responseData?.business || null);
      setLoading(false);
    } catch (err) {
      const errorMessage = err.response?.data?.responseMessage?.[0] ||
        err.message ||
        "Failed to fetch business profile";
      setError(errorMessage);
      toast.error(errorMessage);
      setLoading(false);

      if (err.response?.status === 401) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("authUser");
        history.push("/login");
      }
    }
  };

  const renderRatingStars = (rating) => {
    const numericRating = parseFloat(rating) || 0;

    return Array(5).fill().map((_, i) => {
      if (i < Math.floor(numericRating)) {
        return <i key={i} className="fas fa-star filled" />;
      } else if (i < numericRating) {
        return <i key={i} className="fas fa-star-half-alt filled" />;
      } else {
        return <i key={i} className="far fa-star" />;
      }
    });
  };

  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setError("No authentication token found. Please log in.");
      toast.error("No authentication token found. Please log in.");
      history.push("/login");
      return;
    }

    // Fetch subscription data first, then business profile
    fetchSubscription(token);
    fetchBusinessProfile(token);
  }, [history, refreshTrigger]);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleSubscriptionSuccess = () => {
    setIsModalOpen(false);
    const token = localStorage.getItem("accessToken");
    if (token) {
      fetchBusinessProfile(token); 
      fetchSubscription(token); // Refresh subscription data
    }
  };

  if (loading) return <div className="bp-loading">Loading...</div>;
  if (error) return <div className="bp-error">{error}</div>;
  if (loadError) return <div className="bp-error">Error loading Google Maps</div>;
  if (!isLoaded) return <div className="bp-loading">Loading map...</div>;

  const coordinates = businessData?.businessLocation?.coordinates;
  const center = coordinates ? { lat: coordinates[1], lng: coordinates[0] } : null;

  const addressParts = [];
  if (businessData?.businessLocation?.streetAddress) {
    addressParts.push(businessData.businessLocation.streetAddress);
  }
  if (businessData?.businessLocation?.city) {
    addressParts.push(businessData.businessLocation.city);
  }
  if (businessData?.businessLocation?.state) {
    addressParts.push(businessData.businessLocation.state);
  }
  if (businessData?.businessLocation?.postalCode) {
    addressParts.push(businessData.businessLocation.postalCode);
  }
  const fullAddress = addressParts.length > 0 ? addressParts.join(", ") : "Not available";

  return (
    <div className="page-content">
      <Container fluid className="mt-5">
        <MetaTags>
          <title>Business Profile</title>
        </MetaTags>
        <ToastContainer position="top-right" autoClose={5000} />

  
        <PlanSelectionModal
          isOpen={isModalOpen && !isRefreshing}
          toggle={() => !isRefreshing && setIsModalOpen(!isModalOpen)}
          isUpgrade={bannerData?.buttonText === "Upgrade Plan"}
          onSubscriptionSuccess={refreshSubscriptionData}
          modalConfig={modalConfig}
        />

{/* Updated banner with new logic */}
        {bannerData && (
  <div className="alert alert-success d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2 mt-3">
    <span className="text-center text-sm-start fs-5 fs-sm-6">
      {bannerData.message}
    </span>
    <Button
      color="secondary"
      onClick={handleBannerButtonClick}
      className="w-20 w-sm-auto"
      disabled={isRefreshing}
    >
      {bannerData.buttonText || "Upgrade Plan"}
    </Button>
  </div>
)}




      <Row className="align-items-center topbp flex-column flex-sm-row gap-2">
  <Col className="d-flex align-items-center justify-content-center justify-content-sm-start">
    <h3 className="bp-title mb-0">Business Profile</h3>
  </Col>
  <Col className="d-flex justify-content-center justify-content-sm-end align-items-center">
    <Button 
      color="primary" 
      className="me-0 me-sm-3" 
      onClick={() => history.push('/business-profile-edit')}
    >
      Edit Info
    </Button>
  </Col>
</Row>


        <div className="bp-separator" />
        <Row>
          <Col xl={12}>
            <div className="bp-wrapper">
              <div className="business-profile-view">
                <div className="profile-header upcard">
                  <div className="row align-items-center">
                    <div className="banner-image-outer">
  {businessData?.bannerImage?.url ? (
    <div className="banner-image-container">
      <img
        src={businessData.bannerImage.url}
        alt="Business banner"
        className="business-banner"
      />
      {businessData?.logoImage?.url && (
        <img
          src={businessData.logoImage.url}
          alt="Business logo"
          className="business-logo"
        />
      )}
    </div>
  ) : (
    <div className="banner-placeholder">No banner available</div>
  )}
</div>

                    <div className="col-12 mt-5 businessuppercard d-flex flex-row flex-sm-row flex-column align-items-center">
                      <div className="logo-container me-3">
                        {businessData?.logoImage?.url ? (
                          <></>
                        ) : (
                          <div className="logo-placeholder">No logo</div>
                        )}
                      </div>
                      <div className="business-info me-md-3">
                        <h1 className="bpname mb-2">{businessData?.businessName || "Not available"}</h1>
                        <div className="profile-field description-text">
                          {businessData?.businessDescription || "Not available"}
                        </div>
                        <div className="mb-2 bplink description-text">
                          {businessData?.businessSlug ? (
                            <a
                              href={businessData.businessSlug}
                              className="bplink"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {businessData.businessSlug}
                            </a>
                          ) : (
                            "Not available"
                          )}
                        </div>
                      </div>
                      <div className="ms-md-auto mt-3 mt-md-0">
                        {businessData?.businessSlug ? (
                          <QRCode className="qrcss" value={businessData.businessSlug} />
                        ) : (
                          <div className="text-muted">Not available</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="profile-grid">
                  <div className="profile-column">
                    <div className="profile-section upcard">
                      <h2 className="bp-subheading">Contact Information</h2>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <div className="profile-field">
                            <label className="bpdetail-label">Phone Number</label>
                            <p className="bpdetail">
                              <i className="fas fa-phone" /> {businessData?.phone || "Not available"}
                            </p>
                          </div>
                        </div>
                        <div className="col-md-6 mb-3">
                          <div className="profile-field">
                            <label className="bpdetail-label">Email</label>
                            <p className="bpdetail">
                              <i className="fas fa-envelope" /> {businessData?.user?.email || "Not available"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="profile-section upcard">
                      <h2 className="bp-subheading">Social Media</h2>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <div className="profile-field">
                            <label className="bpdetail-label">Instagram</label>
                            <p className="bpdetail">
                              {businessData?.instagramId ? (
                                <a
                                  href={businessData.instagramId}
                                  className="bp-link"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <i className="fab fa-instagram" /> {businessData.instagramId}
                                </a>
                              ) : (
                                "Not available"
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="col-lg-6 col-md-12 mb-3">
                          <div className="profile-field">
                            <label className="bpdetail-label">Website</label>
                            <p className="bpdetail">
                              {businessData?.websiteLink ? (
                                <a
                                  href={businessData.websiteLink}
                                  className="bp-link"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <i className="fas fa-link" /> {businessData.websiteLink}
                                </a>
                              ) : (
                                "Not available"
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="profile-section upcard">
                      <h2 className="bp-subheading">Operating Hours</h2>
                      <div className="profile-field">
                        <p className="bpdetail" style={{ whiteSpace: "pre-line" }}>
                          {businessData?.operatingHours || "Not available"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="profile-column mt-4">
                    <div className="profile-section upcard">
                      <h2 className="bp-subheading">Location</h2>
                      <p className="bpdetail">
                        <i className="fa fa-location-dot me-3" />
                        {fullAddress}
                      </p>
                      {center ? (
                        <div className="profile-field">
                          <GoogleMap
                            mapContainerStyle={mapContainerStyle}
                            center={center}
                            zoom={13}
                          >
                            <Marker position={center} />
                          </GoogleMap>
                        </div>
                      ) : (
                        <p className="bpdetail">Map not available</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="profile-section upcard ratings-section mt-4">
                  <h2 className="bp-subheading">Ratings & Reviews</h2>
                  {businessData?.reviews?.length > 0 ? (
                    <div className="ratings-list">
                      {businessData.reviews.map((review) => (
                        <div key={review.id} className="rating-item">
                          <div className="rating-header">
                            <strong>{review.user?.name || "Anonymous"}</strong>
                            <span className="rating-date">
                              {formatDate(review.createdAt)}
                            </span>
                          </div>
                          <div className="rating-stars">
                            {renderRatingStars(review.rating)}
                          </div>
                          {review.comment && (
                            <div className="rating-comment">
                              <p>{review.comment}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-ratings">No reviews yet.</p>
                  )}
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default BusinessProfile;